import {
	concatWith,
	defer,
	distinctUntilChanged,
	exhaustMap,
	filter,
	fromEvent,
	ignoreElements,
	map,
	merge,
	of,
	takeUntil,
	tap,
	type Observable,
} from 'rxjs'

/** One emission when a press on the handle becomes a drag, one when the session ends. */
export type DragOffsetPhase = { dragging: true } | { dragging: false; moved: boolean }

type Offset = { dx: number; dy: number }

/** Below this movement (px), a press on the handle is a tap, not a drag. */
const TAP_THRESHOLD_PX = 5

const DRAGGING: DragOffsetPhase = { dragging: true }

function readOffset(storageKey: string): Offset | null {
	try {
		const raw = sessionStorage.getItem(storageKey)
		if (!raw) return null
		const parsed: unknown = JSON.parse(raw)
		if (
			typeof parsed === 'object' &&
			parsed !== null &&
			'dx' in parsed &&
			'dy' in parsed &&
			typeof parsed.dx === 'number' &&
			typeof parsed.dy === 'number'
		)
			return { dx: parsed.dx, dy: parsed.dy }
		return null
	} catch {
		return null
	}
}

function writeOffset(storageKey: string, dx: number, dy: number): void {
	try {
		sessionStorage.setItem(storageKey, JSON.stringify({ dx, dy }))
	} catch {
		// Storage unavailable (private mode, quota) — the drag still tracks the pointer live.
	}
}

/**
 * Grip-drag of `host` as a translate offset from wherever it rests, clamped to
 * the viewport, remembered per `storageKey` for the tab's lifetime
 * (`sessionStorage`), re-clamped when the window resizes. The consumer keeps
 * every other decision: where the host rests, what the grip looks like, what a
 * tap on the grip means (`moved: false` on the settling emission).
 *
 * The session ends on `pointerup` OR `pointercancel` — touch and stylus fire
 * only the latter when the OS takes the gesture (an incoming call, a second
 * finger, a system edge swipe), and a session that waited for `pointerup`
 * alone would keep tracking a pointer that no longer exists.
 *
 * Side effects — applying the stored offset, the transform writes, the
 * resize listener — begin at subscription, so a host that gains or loses its
 * grip can switch this on and off with `switchMap`.
 */
export function dragOffset(host: HTMLElement, handle: HTMLElement, storageKey: string): Observable<DragOffsetPhase> {
	let dx = 0
	let dy = 0

	const apply = (nextDx: number, nextDy: number) => {
		dx = nextDx
		dy = nextDy
		host.style.transform = `translate(${dx}px, ${dy}px)`
	}

	/** The host's rect with the current offset removed — what it would occupy untouched. */
	const baseRect = () => {
		const rect = host.getBoundingClientRect()
		return new DOMRect(rect.left - dx, rect.top - dy, rect.width, rect.height)
	}

	const clamp = (base: DOMRect, candidateDx: number, candidateDy: number): Offset => ({
		dx: Math.min(Math.max(candidateDx, -base.left), window.innerWidth - base.right),
		dy: Math.min(Math.max(candidateDy, -base.top), window.innerHeight - base.bottom),
	})

	return defer(() => {
		const stored = readOffset(storageKey)
		if (stored) {
			const clamped = clamp(baseRect(), stored.dx, stored.dy)
			apply(clamped.dx, clamped.dy)
		}

		const drag$ = fromEvent<PointerEvent>(handle, 'pointerdown').pipe(
			filter(e => e.button === 0),
			tap(e => {
				e.preventDefault()
				try {
					handle.setPointerCapture(e.pointerId)
				} catch {
					// A fast tap may already have released — harmless.
				}
			}),
			exhaustMap(start => {
				const sessionDx = dx
				const sessionDy = dy
				const base = baseRect()
				let moved = false
				return fromEvent<PointerEvent>(window, 'pointermove').pipe(
					filter(e => e.pointerId === start.pointerId),
					map(move => ({
						rawDx: sessionDx + (move.clientX - start.clientX),
						rawDy: sessionDy + (move.clientY - start.clientY),
					})),
					filter(
						({ rawDx, rawDy }) =>
							moved ||
							Math.abs(rawDx - sessionDx) >= TAP_THRESHOLD_PX ||
							Math.abs(rawDy - sessionDy) >= TAP_THRESHOLD_PX,
					),
					tap(({ rawDx, rawDy }) => {
						moved = true
						const clamped = clamp(base, rawDx, rawDy)
						apply(clamped.dx, clamped.dy)
					}),
					map(() => DRAGGING),
					distinctUntilChanged(),
					takeUntil(
						merge(
							fromEvent<PointerEvent>(window, 'pointerup'),
							fromEvent<PointerEvent>(window, 'pointercancel'),
						).pipe(filter(e => e.pointerId === start.pointerId)),
					),
					concatWith(
						defer(() => {
							if (moved) writeOffset(storageKey, dx, dy)
							return of<DragOffsetPhase>({ dragging: false, moved })
						}),
					),
				)
			}),
		)

		// A persisted offset must not strand the host off-screen after the viewport shrinks.
		const resize$ = fromEvent<Event>(window, 'resize').pipe(
			map((): Offset => clamp(baseRect(), dx, dy)),
			filter((clamped: Offset) => clamped.dx !== dx || clamped.dy !== dy),
			tap((clamped: Offset) => {
				apply(clamped.dx, clamped.dy)
				writeOffset(storageKey, clamped.dx, clamped.dy)
			}),
			ignoreElements(),
		)

		return merge(drag$, resize$)
	})
}
